import posthog from "posthog-js";

const APP_NAME = "marketplace";

const isBrowser = (): boolean => typeof window !== "undefined";

const isTrackingDisabled = (): boolean => {
  if (!isBrowser()) return true;
  try {
    return !!window.localStorage.getItem("disable_tracking");
  } catch {
    return false;
  }
};

let initialised = false;

export function initAnalytics(): void {
  if (!isBrowser() || initialised || isTrackingDisabled()) return;

  const token = import.meta.env.REACT_APP_TRACKING_TOKEN;
  const apiHost = import.meta.env.REACT_APP_TRACKING_URL_2;
  if (!token || !apiHost) return;

  posthog.init(token, {
    api_host: apiHost,
    persistence: "cookie",
    cross_subdomain_cookie: true,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
  });
  posthog.register({
    app: APP_NAME,
    raytioEnv: import.meta.env.REACT_APP_ENVIRONMENT,
  });

  // eslint-disable-next-line fp/no-mutation
  initialised = true;
}

export function identifyUser(sub: string, email: string): void {
  if (!isBrowser() || !initialised || isTrackingDisabled()) return;
  if (!sub) return;
  posthog.identify(sub, { $set: { email } });
}

export function resetUser(): void {
  if (!isBrowser() || !initialised || isTrackingDisabled()) return;
  posthog.reset();
}

export function capturePageview(): void {
  if (!isBrowser() || !initialised || isTrackingDisabled()) return;
  posthog.capture("$pageview");
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!isBrowser() || !initialised || isTrackingDisabled()) return;
  posthog.capture(event, properties);
}

/** Marketplace event helpers per the epic taxonomy. */
export const marketplaceEvents = {
  listingViewed(listingId: string, category: string): void {
    captureEvent("listing_viewed", {
      listing_id: listingId,
      category,
    });
  },
  bookingStarted(listingId: string, category: string): void {
    captureEvent("booking_started", {
      listing_id: listingId,
      category,
    });
  },
  bookingCompleted(listingId: string, category: string): void {
    captureEvent("booking_completed", {
      listing_id: listingId,
      category,
    });
  },
  checkoutStarted(orderId: string): void {
    captureEvent("checkout_started", { order_id: orderId });
  },
  paymentSucceeded(orderCode: string | null): void {
    captureEvent("payment_succeeded", { order_code: orderCode });
  },
  fulfillmentConfirmed(orderCode: string | null): void {
    captureEvent("fulfillment_confirmed", { order_code: orderCode });
  },
};
